import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useResizeObserver, useUi } from '@weareconceptstudio/core';

//* Styles
import TitleAnimStyle from './style';

//* Helpers animation functions
import { generateId, getComputedStyleForSpan, splitTextToLines } from '../helpers';
import CustomEase from 'gsap/CustomEase';

gsap.registerPlugin(ScrollTrigger, CustomEase);

const TitleAnim = ({ children, load, delay, duration, textCenter = true, animRefTrigger, trigger, delayAnim, className }) => {
	const { winWidth } = useUi();

	//! Refs
	const blockRef = useRef();
	const triggerRef = useRef();
	const animRef = useRef();
	const colorMatrixTweenRef = useRef();
	const deviationRef = useRef();
	const reverseTrigger = useRef();
	const reverseStart = useRef(true);
	const timeoutRef = useRef();

	//! State
	const [spans, setSpans] = useState([]);
	const [size, setSize] = useState({ width: 0, height: 0 });

	const resize = useResizeObserver(document.querySelector('body'));

	useEffect(() => {
		if (blockRef.current) {
			const { width, height } = blockRef.current.getBoundingClientRect();
			setSize({ width, height });

			const spanEls = Array.from(blockRef.current.querySelectorAll('span, p, h1, h2, h3, h4, h5, h6'));

			const spansData = spanEls.map((el) => ({
				text: el.textContent,
				style: getComputedStyleForSpan(el),
			}));

			setSpans(spansData);
		}
	}, [children, winWidth]);

	const lines = splitTextToLines(spans, size.width);
	let fontSizes = lines.map((line) => (line.styles.length && line.styles[0].fontSize ? parseFloat(line.styles[0].fontSize) : 16));
	const lineHeight = Math.max(...fontSizes);

	const totalTextHeight = lineHeight * lines.length;

	const isSafari = typeof window !== 'undefined' ? /^((?!chrome|android).)*safari/i.test(navigator.userAgent) : false;

	const delta = (size.height - totalTextHeight) / lines.length;

	const yStart = lineHeight - delta / 2;

	const uniqueId = generateId();

	//! Animate
	useEffect(() => {
		if (load && animRef.current) {
			timeoutRef.current = setTimeout(() => {
				const colorMatrix = animRef.current.querySelector('#colorMatrix');
				const blurFilter = animRef.current.querySelector('#blurFilter');

				const DEVIATION = window.innerWidth > 1024 ? 60 : 30;

				triggerRef.current = ScrollTrigger.create({
					start: `top ${trigger || '80%'}`,
					end: 'top top',
					// markers: true,
					trigger: animRefTrigger?.current || animRef.current,

					onEnter: () => {
						if (reverseStart.current) {
							reverseStart.current = false;
							gsap.set(animRef.current, {
								opacity: 1,
							});

							deviationRef.current = gsap.fromTo(
								blurFilter,
								{ attr: { stdDeviation: DEVIATION } },
								{
									attr: { stdDeviation: 0 },
									duration: duration || 0.7,
									ease: CustomEase.create('custom', 'M0,0 C0.033,0.661 0.734,0.965 1,1 '),
									// delay: delay || 0,
								}
							);

							const durationCalc = duration || 0.65;
							const delayed = durationCalc;
							gsap.to(colorMatrix, {
								attr: { values: '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0' },
								delay: delayed,
								ease: 'power2.out',
								duration: 0.01,
								onStart: () => {
									if (colorMatrixTweenRef.current) {
										colorMatrixTweenRef.current.kill();
									}
								},
							});

							gsap.to(blurFilter, {
								attr: { stdDeviation: 0 },
								duration: 0.01,
								ease: 'power2.out',
								delay: delayed,
								onStart: () => {
									if (deviationRef.current) {
										deviationRef.current.kill();
									}
								},
							});
						}
					},
				});

				reverseTrigger.current = ScrollTrigger.create({
					start: 'top bottom',
					end: 'top top',
					// markers: true,
					trigger: animRefTrigger?.current || animRef.current,
					onLeaveBack: () => {
						reverseStart.current = true;
						gsap.killTweensOf([colorMatrix, blurFilter, animRef.current]);
						gsap.set(colorMatrix, {
							attr: { values: '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 5 -2' },
						});
						gsap.set(blurFilter, {
							attr: { stdDeviation: 0 },
						});
						gsap.set(animRef.current, {
							opacity: 0,
						});
					},
				});
			}, delayAnim || 0);
		}
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
			if (reverseTrigger.current) {
				reverseTrigger.current.kill();
			}
		};
	}, [load, resize, animRefTrigger, delay, delayAnim, duration, trigger]);

	return (
		<TitleAnimStyle className={className || ''}>
			<div
				className='opacity-zero'
				ref={animRef}
			>
				<svg
					width={size.width}
					height={size.height}
					viewBox={`0 0 ${size.width} ${size.height}`}
				>
					<defs>
						<filter
							id={`anim-title-${uniqueId}`}
							x='0%'
							y='0%'
							width='100%'
							height='100%'
							colorInterpolationFilters='sRGB'
						>
							<feGaussianBlur
								id='blurFilter'
								in='SourceGraphic'
								stdDeviation='0'
								result='blur'
							/>
							<feColorMatrix
								id='colorMatrix'
								// in={'blur'}
								// in='blur'
								// in={'luminanceToAlpha'}
								type='matrix'
								values='
							1 0 0 0 0
							0 1 0 0 0
							0 0 1 0 0
							0 0 0 5 -2
						'
								result='goo'
							/>
							<feComposite
								in='SourceGraphic'
								operator='atop'
							/>
						</filter>
					</defs>
					{lines.map((line, idx) => {
						return (
							<text
								key={idx}
								x={textCenter ? size.width / 2 : 0}
								// y={yStart}
								y={yStart * (idx + 1) + (idx != 0 ? (delta + delta / 2) * idx : 0)}
								textAnchor={textCenter ? 'middle' : 'unset'}
								// dominantBaseline='text-before-edge'
								filter={`url(#anim-title-${uniqueId})`}
							>
								{line.words.map((word, i) => {
									return word == ' ' && isSafari ? (
										<tspan
											key={i}
											style={{ ...line.styles[i], fontSize: parseFloat(line.styles[i].fontSize) / 1.15 }}
										>
											{word}
										</tspan>
									) : (
										<tspan
											key={i}
											style={line.styles[i]}
										>
											{word}
										</tspan>
									);
								})}
							</text>
						);
					})}
				</svg>
				<div
					ref={blockRef}
					className='wrapper-styling-block'
				>
					{children}
				</div>
			</div>
		</TitleAnimStyle>
	);
};

export default TitleAnim;
